import { useReactiveVar } from '@apollo/client';
import { Meta, theme } from 'aimo-ui';
import SkeletonLoaderRow, {
  SkeletonWrapper,
} from 'components/loading/SkeletonLoaderRow';
import FlatListCustom from 'components/sheets/FlatListCustom';
import AccountContext from 'context/AccountContext';
import { useParkingZoneSearch } from 'hooks/useParkingZoneSearch';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LatLng } from 'react-native-maps';
import { useUiStore } from 'stores/uiStore';
import { featureCountryCode } from 'states/persistInStorage';
import { Divider } from 'styles/CommonLayoutStyles';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { ParkZone } from 'types/generatedSchemaTypes';
import {
  getZonesWithVisibility,
  parkMapParkzoneFilter,
  quickMenuSort,
  sortInMainMapViewByZoneAndBenefit,
} from 'utils/parkUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import SearchItem from '../SearchItem';
import SearchItemSkeleton from '../SearchItemSkeleton';
import { MarginView, Row } from '../styles/styles';

type SearchResultsListProps = {
  searchTerm: string;
  currentLocation: LatLng | null;
  withLTP?: boolean;
};

const ItemSeparator = () => <Divider marginVertical={10} />;
const ListFooter = () => <MarginView />;

const ParkzoneResultsList: React.FC<SearchResultsListProps> = ({
  searchTerm,
  currentLocation,
  withLTP,
}) => {
  const { t } = useTranslation();
  const {
    customer: { householdBenefits, poolingBenefits, permits },
  } = useContext(AccountContext);
  const appCountry = useReactiveVar(featureCountryCode);
  const selectedQuickAccessItem = useUiStore(
    (state) => state.selectedParkScreenQuickAccessItem
  );

  const { parkZones, loading, evConnectors } = useParkingZoneSearch({
    searchTerm,
    withLTP,
  });

  const hasConnectors = (evConnectors?.searchEvConnectors?.length ?? 0) !== 0;
  const searchResults = useMemo(() => {
    if (parkZones && searchTerm.length > 0) {
      return withLTP
        ? [
            ...getZonesWithVisibility(
              (parkZones.searchParkZonesLTPByText?.content ?? []) as ParkZone[],
              parkZones.searchParkZonesLTPByText?.pmcVisibilities,
              householdBenefits,
              null,
              appCountry,
              true
            ),
          ]
        : [
            ...parkMapParkzoneFilter(
              (parkZones.searchParkZonesByText?.content ?? []) as ParkZone[],
              [...(householdBenefits || []), ...(poolingBenefits || [])],
              permits
            )
              .sort((a, b) =>
                quickMenuSort(
                  a,
                  b,
                  selectedQuickAccessItem,
                  currentLocation,
                  withLTP
                )
              )
              .sort((a, b) => {
                return sortInMainMapViewByZoneAndBenefit(
                  a,
                  b,
                  householdBenefits,
                  currentLocation
                );
              }),
          ];
    }
    return [];
  }, [
    currentLocation,
    householdBenefits,
    poolingBenefits,
    permits,
    parkZones,
    searchTerm.length,
    selectedQuickAccessItem,
    withLTP,
    appCountry,
  ]);

  if (loading) {
    return (
      <>
        <Row mt={15} mb={20}>
          <SkeletonWrapper width={80}>
            <SkeletonLoaderRow height={15} />
          </SkeletonWrapper>
        </Row>
        <SearchItemSkeleton lastElement />
      </>
    );
  }

  if (searchResults.length === 0) {
    return null;
  }

  return (
    <ColumnWithMargins mb={hasConnectors ? 0 : 40}>
      <Row mt={15} mb={15}>
        <Meta
          {...testIdentifiers('results-label')}
          color={theme.colors.gray300}>
          {t('search.results', { amount: searchResults.length })}
        </Meta>
      </Row>
      <FlatListCustom
        ItemSeparatorComponent={ItemSeparator}
        data={searchResults}
        renderItem={({ item }) => (
          <SearchItem
            item={item}
            currentLocation={currentLocation}
            withLTP={withLTP}
          />
        )}
        ListFooterComponent={hasConnectors ? undefined : ListFooter}
        keyExtractor={(item, index) => `${item?.uid}-${index}`}
        {...testIdentifiers('search-results-list')}
      />
    </ColumnWithMargins>
  );
};

export default ParkzoneResultsList;
